import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuthStore } from "../../Store/auth";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  User,
  Mail as MailIcon,
  Phone as PhoneIcon,
  MessageCircle as WhatsAppIcon,
  MapPin,
  Link as LinkIcon,
  Copy as CopyIcon,
  Download as DownloadIcon,
  RefreshCcw,
  CheckCircle2,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { API_ORIGIN } from "../../config/api";

const API = API_ORIGIN;

// -------- helpers
const normalize = (x) => (Array.isArray(x) ? x : x?.$values ?? []);
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const daysBetween = (toISO) => {
  if (!toISO) return null;
  const now = new Date();
  const to = new Date(toISO);
  return Math.ceil((to.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

// Existing early access is shown only for coaches who already have it.
function deriveSubscription(coach) {
  if (!coach) {
    return {
      state: "none",
      label: "No Subscription",
      planLabel: "Free",
      daysLeft: null,
      expiresAt: null,
      isPaidStripe: false,
    };
  }

  // Tier
  const tier = coach.subscriptionTier ?? coach.subscriptionPlan ?? null;

  let planLabel = "Free";
  if (tier === 1 || tier === "Basic" || tier === "basic") {
    planLabel = "Legacy Basic";
  } else if (
    tier === 2 ||
    tier === "Pro" ||
    tier === "Standard" ||
    tier === "pro"
  ) {
    planLabel = "Lead Access";
  }

  // Status
  const rawStatus = coach.subscriptionStatus ?? coach.subscription_status ?? null;
  const s = String(rawStatus ?? "").toLowerCase();

  let state = "none";
  let label = "No Subscription";

  if (s === "1" || s === "active") {
    state = "active";
    label = "Active";
  } else if (
    s === "2" ||
    s === "pastdue" ||
    s === "past_due" ||
    s === "past-due"
  ) {
    state = "past_due";
    label = "Past Due";
  } else if (s === "3" || s === "canceled" || s === "cancelled") {
    state = "canceled";
    label = "Canceled";
  } else if (s === "4" || s === "expired") {
    state = "expired";
    label = "Expired";
  }

  const expiresAt =
    coach.currentPeriodEndUtc ||
    coach.subscriptionExpiresAtUtc ||
    coach.subscriptionExpiryUtc ||
    null;

  const daysLeft = expiresAt ? daysBetween(expiresAt) : null;

  // Stripe paid subscription?
  const stripeSubId =
    coach.stripeSubscriptionId ||
    coach.stripe_subscription_id ||
    coach.subscriptionId ||
    null;
  const isPaidStripe = !!stripeSubId;

  // ✅ Early Access FREE: If no Stripe subscription AND expiresAt is in the future => Early Access
  const now = new Date();
  if (!isPaidStripe && expiresAt) {
    const exp = new Date(expiresAt);
    if (exp > now) {
      state = "free";
      label = `Early Access (Free • ends ${formatDate(expiresAt)})`;
      planLabel = "Early Access";
    } else {
      state = "expired";
      label = "Free listing; lead access unavailable";
      planLabel = "Free";
    }
  }

  return { state, label, planLabel, daysLeft, expiresAt, isPaidStripe };
}

export default function Profile() {
  const { token, coachId, logout } = useAuthStore();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [coach, setCoach] = useState(null);
  const [error, setError] = useState("");

  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const subscription = useMemo(() => deriveSubscription(coach), [coach]);

  // ACTIONS
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  const openWhatsApp = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");
    if (!digits) return;
    const msg = encodeURIComponent(
      `Hi ${coach?.fullName || ""}, I'm interested in training.`
    );
    window.open(`https://wa.me/${digits}?text=${msg}`, "_blank");
  };

  const openMaps = () => {
    const q = [coach?.area, coach?.city, coach?.country].filter(Boolean).join(", ");
    if (!q) return;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`,
      "_blank"
    );
  };

  const profileLink = () => `${window.location.origin}/coach/${coachId}`;

  const downloadVCard = () => {
    const lines = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${coach?.fullName || ""}`,
      `EMAIL;TYPE=INTERNET:${coach?.email || ""}`,
      coach?.phoneNumber ? `TEL;TYPE=CELL:${coach.phoneNumber}` : "",
      `ORG:PTfinderNow`,
      coach?.speciality?.name ? `TITLE:${coach.speciality.name}` : "",
      `ADR;TYPE=WORK:;;${coach?.area || ""};${coach?.city || ""};;${coach?.country || ""};`,
      `URL:${profileLink()}`,
      "END:VCARD",
    ].filter(Boolean);

    const blob = new Blob([lines.join("\r\n")], {
      type: "text/vcard;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(coach?.fullName || "coach").replace(/\s+/g, "_")}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const manageSubscription = () => {
    navigate("/dashboard/subscription");
  };

  // FETCH
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/api/Coaches/${coachId}`, auth);
        if (!mounted) return;
        setCoach(res.data);
      } catch (e) {
        if (e?.response?.status === 401) logout?.();
        setError("Failed to load profile.");
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [coachId, auth, logout]);

  // DISPLAY NAMES (fallback to raw strings)
  const categoryName =
    (typeof coach?.category === "string" && coach?.category) ||
    coach?.category?.name ||
    coach?.category ||
    "";
  const specialityName = coach?.speciality?.name || coach?.speciality || "";
  const countryName = coach?.country || "";
  const cityName = coach?.city || "";
  const areaName = coach?.area || "";

  const isActiveOrFree =
    subscription.state === "active" ||
    subscription.state === "past_due" ||
    subscription.state === "free";

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            My Profile
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            This is how students see you on PTfinderNow. Keep your details up to
            date.
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50"
        >
          <RefreshCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Subscription banner only */}
      {!isActiveOrFree && (
        <div className="mb-3 rounded-xl border border-indigo-100 bg-indigo-50 p-3 flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-indigo-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-indigo-900">
              Lead access locked
            </div>
            <div className="text-sm text-indigo-800">
              Your public listing stays visible. Subscribe to Lead Access to
              read and reply to client inquiries.
            </div>
          </div>
          <button
            onClick={manageSubscription}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
          >
            View Lead Access
          </button>
        </div>
      )}

      {/* Card */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* HEADER / BANNER */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-4 sm:px-6 py-5 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* avatar */}
            <div className="shrink-0">
              {loading ? (
                <div className="w-24 h-24 rounded-full ring-4 ring-white/20 overflow-hidden">
                  <Skeleton className="w-full h-full" />
                </div>
              ) : coach?.profileImage ? (
                <img
                  src={coach.profileImage}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover ring-4 ring-white/60 shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 rounded-full ring-4 ring-white/40 bg-white/10 grid place-items-center text-white/80">
                  <User className="w-10 h-10" />
                </div>
              )}
            </div>

            {/* identity */}
            <div className="flex-1 min-w-0">
              {loading ? (
                <>
                  <Skeleton className="h-6 w-48 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-xl sm:text-2xl font-bold truncate">
                      {coach?.fullName || "—"}
                    </h3>
                  </div>
                  <p className="text-sm text-white/80 mt-1">
                    {(categoryName || "—") +
                      (specialityName ? ` • ${specialityName}` : "")}
                  </p>
                </>
              )}
            </div>

            {/* quick share */}
            {!loading && (
              <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
                <button
                  onClick={() => copy(profileLink())}
                  className="px-3 py-2 rounded-lg bg-white/10 border border-white/30 text-xs sm:text-sm inline-flex items-center gap-2 hover:bg-white/20"
                >
                  <LinkIcon className="w-4 h-4" /> Copy profile link
                </button>
                <button
                  onClick={downloadVCard}
                  className="px-3 py-2 rounded-lg bg-white/10 border border-white/30 text-xs sm:text-sm inline-flex items-center gap-2 hover:bg-white/20"
                >
                  <DownloadIcon className="w-4 h-4" /> vCard
                </button>
              </div>
            )}
          </div>
        </div>

        {/* BODY */}
        <div className="p-4 sm:p-6">
          {/* Subscription card */}
          <div className="grid grid-cols-1 gap-4">
            <section className="border border-gray-100 rounded-xl p-4 bg-gray-50/40">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-indigo-600" />
                <h4 className="font-semibold text-gray-900">Subscription</h4>
              </div>

              <div className="mt-2 text-sm text-gray-700">
                <div className="font-medium">
                  {subscription.planLabel} — {subscription.label}
                </div>

                {subscription.daysLeft !== null && subscription.daysLeft >= 0 && (
                  <div className="text-gray-500 flex items-center gap-2">
                    {subscription.state === "free" ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : subscription.state === "past_due" ? (
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-500" />
                    )}
                    <span>
                      {subscription.state === "free"
                        ? "Free access ends in "
                        : "Renews in "}
                      <b>
                        {subscription.daysLeft} day
                        {subscription.daysLeft === 1 ? "" : "s"}
                      </b>
                    </span>
                  </div>
                )}
              </div>

              <button
                onClick={manageSubscription}
                className="mt-3 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
              >
                Manage subscription
              </button>
            </section>
          </div>

          {/* info grid */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* CONTACT */}
            <section className="border border-gray-100 rounded-xl p-4 bg-white">
              <h4 className="font-semibold mb-3 text-gray-900">Contact</h4>
              {loading ? (
                <>
                  <Skeleton className="h-4 w-64 mb-2" />
                  <Skeleton className="h-4 w-48 mb-2" />
                  <Skeleton className="h-4 w-40" />
                </>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <MailIcon className="w-4 h-4 text-gray-500" />
                    <a
                      className="text-blue-600 hover:underline break-all"
                      href={`mailto:${coach?.email || ""}`}
                    >
                      {coach?.email || "—"}
                    </a>
                    {coach?.email && (
                      <button
                        onClick={() => copy(coach.email)}
                        className="ml-auto text-gray-400 hover:text-gray-700"
                      >
                        <CopyIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <PhoneIcon className="w-4 h-4 text-gray-500" />
                    {coach?.phoneNumber ? (
                      <a
                        className="text-blue-600 hover:underline"
                        href={`tel:${coach.phoneNumber}`}
                      >
                        {coach.phoneNumber}
                      </a>
                    ) : (
                      "—"
                    )}
                    {coach?.phoneNumber && (
                      <button
                        onClick={() => copy(coach.phoneNumber)}
                        className="ml-auto text-gray-400 hover:text-gray-700"
                      >
                        <CopyIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {coach?.email && (
                      <a
                        href={`mailto:${coach.email}`}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-2 text-xs sm:text-sm"
                      >
                        <MailIcon className="w-4 h-4" /> Email
                      </a>
                    )}
                    {coach?.phoneNumber && (
                      <>
                        <a
                          href={`tel:${coach.phoneNumber}`}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-2 text-xs sm:text-sm"
                        >
                          <PhoneIcon className="w-4 h-4" /> Call
                        </a>
                        <button
                          onClick={() => openWhatsApp(coach.phoneNumber)}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-2 text-xs sm:text-sm"
                        >
                          <WhatsAppIcon className="w-4 h-4" /> WhatsApp
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* LOCATION */}
            <section className="border border-gray-100 rounded-xl p-4 bg-white">
              <h4 className="font-semibold mb-3 text-gray-900">Location</h4>
              {loading ? (
                <>
                  <Skeleton className="h-4 w-44 mb-2" />
                  <Skeleton className="h-4 w-40 mb-2" />
                </>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span>
                      {areaName || "—"} {cityName ? `• ${cityName}` : ""}{" "}
                      {countryName ? `• ${countryName}` : ""}
                    </span>
                  </div>
                  {(areaName || cityName || countryName) && (
                    <button
                      onClick={openMaps}
                      className="mt-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs sm:text-sm inline-flex items-center gap-2"
                    >
                      <MapPin className="w-4 h-4" /> Open in Maps
                    </button>
                  )}
                </div>
              )}
            </section>

            {/* ABOUT */}
            <section className="border border-gray-100 rounded-xl p-4 bg-white sm:col-span-2">
              <h4 className="font-semibold mb-3 text-gray-900">About</h4>
              {loading ? (
                <>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </>
              ) : (
                <p className="text-sm text-gray-800 whitespace-pre-line">
                  {coach?.description ||
                    "Add a short description about your experience, style and what students can expect."}
                </p>
              )}

              {!loading && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {categoryName && (
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                      {categoryName}
                    </span>
                  )}
                  {specialityName && (
                    <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                      {specialityName}
                    </span>
                  )}
                  {countryName && (
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-50 text-gray-700 border border-gray-200">
                      {countryName}
                    </span>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* footer quick tools */}
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={() => copy(profileLink())}
              className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm inline-flex items-center gap-2"
            >
              <LinkIcon className="w-4 h-4" /> Copy profile link
            </button>

            <button
              onClick={downloadVCard}
              className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm inline-flex items-center gap-2"
            >
              <DownloadIcon className="w-4 h-4" /> vCard
            </button>

            <button
              onClick={manageSubscription}
              className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm inline-flex items-center gap-2"
            >
              <CreditCard className="w-4 h-4" /> Subscription
            </button>
          </div>

          {error && (
            <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
